<script setup lang="ts">
import { computed, ref, nextTick } from 'vue'
import { GripVertical, Pencil } from 'lucide-vue-next'
import BBtn from '@lib/components/base/BBtn.vue'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import { type ClipboardTicket } from '@/stores/clipboard'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ code: ClipboardTicket }>()
const emit = defineEmits<{
    (e: 'remove', id: number): void
    (e: 'update-print-label', id: number, label: string): void
}>()

const { t } = useI18n()

// QR is the bare EAN code — read as a ticket by the scanner/reader, no prefix,
// no deep-link wrapping.
const qrCode = computed(() => props.code.code)

const printLabel = computed(() => props.code.printLabel ?? '')

const editingLabel = ref(false)
const labelDraft = ref('')
const labelInputEl = ref<HTMLInputElement | null>(null)

async function startEditLabel() {
    labelDraft.value = printLabel.value
    editingLabel.value = true
    await nextTick()
    labelInputEl.value?.focus()
    labelInputEl.value?.select()
}

function saveLabel() {
    // Guard against double-emit: Enter triggers saveLabel(), then the input is
    // unmounted via v-if and its @blur fires saveLabel() again.
    if (!editingLabel.value) return
    editingLabel.value = false
    emit('update-print-label', props.code.id, labelDraft.value.trim())
}

function cancelEditLabel() {
    editingLabel.value = false
}

function onLabelKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
        e.preventDefault()
        saveLabel()
    } else if (e.key === 'Escape') {
        e.preventDefault()
        cancelEditLabel()
    }
}
</script>

<template>
    <!--
        Ticket card. Bare EAN QR; no merge/split/login, no drop overlay. Only the
        Sortable handle drags (reorder); the rest of the card is inert.
    -->
    <div
        class="border border-gray-200 rounded-12 p-3 flex items-stretch gap-3 overflow-hidden relative bg-amber-50"
        :data-id="code.id"
    >
        <div
            class="clipboard-card-handle absolute top-1 left-1 p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            :title="t('clipboard.dragHandle')"
        >
            <GripVertical class="w-4 h-4" />
        </div>

        <div class="flex-1 flex flex-col items-center gap-2 min-w-0 w-full">
            <QrPreview :code="qrCode" label="" :size="160" />

            <!-- Ticket code (bare EAN), shown below the QR. -->
            <div class="text-sm font-semibold font-mono text-center break-all">
                {{ code.code }}
            </div>

            <!-- Custom print label (print:hidden — the print sheet renders it itself). -->
            <div class="w-full print:hidden">
                <input
                    v-if="editingLabel"
                    ref="labelInputEl"
                    v-model="labelDraft"
                    type="text"
                    class="w-full text-xs text-ink bg-white border border-teal rounded px-2 py-1 outline-none text-center"
                    :placeholder="t('clipboard.printLabel.placeholder')"
                    @keydown="onLabelKeydown"
                    @blur="saveLabel"
                />
                <button
                    v-else
                    type="button"
                    data-test="ticket-print-label-edit"
                    class="w-full inline-flex items-center justify-center gap-1 text-xs rounded px-2 py-1 hover:bg-black/5 transition-colors"
                    :class="printLabel ? 'text-ink font-medium' : 'text-gray-400 italic'"
                    :title="t('clipboard.printLabel.hint')"
                    @click="startEditLabel"
                >
                    <Pencil class="w-3 h-3 shrink-0" />
                    <span class="truncate">{{ printLabel || t('clipboard.printLabel.add') }}</span>
                </button>
            </div>

            <div class="mt-auto flex items-center gap-1 flex-wrap justify-center">
                <BBtn
                    severity="secondary"
                    size="small"
                    data-test="ticket-remove"
                    @click="$emit('remove', code.id)"
                >
                    {{ t('clipboard.remove') }}
                </BBtn>
            </div>
        </div>
    </div>
</template>
